// KNSAIX Security Center terminology helpers
// Maps machine keys to human-readable concepts for UI presentation.
// All strings are looked up via i18n; this module only provides the keys.

import { useAppI18n, I18nKey } from '../../lib/appI18n';
/**
 * Map capability keys to their resource type (domain) for grouping.
 * Example: 'CRM.READ' → 'CRM'
 */
const CAPABILITY_DOMAIN_MAP: Record<string, string> = {
  // Security Center
  'SECURITY.CENTER.READ': 'SECURITY_CENTER',
  'SECURITY.ACCESS.MANAGE': 'SECURITY_CENTER',
  // CRM
  'CRM.READ': 'CRM',
  'CRM.WRITE': 'CRM',
  'CRM.EXPORT': 'CRM',
  // Appointments
  'APPOINTMENTS.READ': 'APPOINTMENTS',
  'APPOINTMENTS.WRITE': 'APPOINTMENTS',
  // Field Service
  'FIELD_SERVICE.READ': 'FIELD_SERVICE',
  'FIELD_SERVICE.WRITE': 'FIELD_SERVICE',
  // AI Console
  'AI.CONSOLE.USE': 'AI_CONSOLE',
  // Provider Market
  'PROVIDER.MARKET.READ': 'PROVIDER_MARKET',
  'PROVIDER.TEST': 'PROVIDER_MARKET',
  'PROVIDER.CONNECT': 'PROVIDER_MARKET',
  // Accounting
  'ACCOUNTING.READ': 'ACCOUNTING',
  'ACCOUNTING.INVOICE.CREATE': 'ACCOUNTING',
  'ACCOUNTING.INVOICE.APPROVE': 'ACCOUNTING',
  // LID
  'LID.USE': 'LID',
};

/**
 * Get the displayable domain name for a capability key.
 * For known domain: returns i18n key for translation (e.g., "domain.crm").
 * For unknown domain: returns the raw capability key unchanged.
 */
export function useCapabilityDomain() {
  const { t } = useAppI18n();
  return (capabilityKey: string) => {
    const domain = CAPABILITY_DOMAIN_MAP[capabilityKey];
    if (domain !== undefined) {
      // Known domain: return i18n key for translation
      return `domain.${domain.toLowerCase()}`;
    }
    // Unknown domain: return raw capability key
    return capabilityKey;
  };
}

/**
 * Map machine event keys to i18n keys for presentation.
 * The UI will look up the key and apply pluralization if needed.
 */
const EVENT_LABEL_MAP: Record<string, I18nKey> = {
  // Authentication
  'security.authentication.succeeded': 'event.authenticationSucceeded',
  'security.authentication.failed': 'event.authenticationFailed',
  // Device session
  'security.jit.requested': 'event.jitRequested',
  'security.jit.decision': 'event.jitDecision',
  // Credential
  'security.credential.renamed': 'eventCredentialRenamed',
  // Recovery
  'security.authority.unlocked': 'eventAuthorityUnlocked',
  // Desktop handoff
  'desktop-handoff.approved': 'eventDesktopHandoffApproved',
  // Session lifecycle
  'security.session.revoked': 'eventSessionRevoked',
  // Add more as needed
};

/**
 * Get the i18n key for an event label.
 * @param t - i18n translation function
 */
export function makeEventLabel(t: (key: I18nKey) => string) {
  return (eventKey: string) => {
    const mapped = EVENT_LABEL_MAP[eventKey] ?? 'event.unknown';
    return t(mapped);
  };
}

/**
 * Get the i18n key for an event label.
 * Uses the i18n context from useAppI18n.
 */
export function useEventLabel() {
  const { t } = useAppI18n();
  return makeEventLabel(t);
}

/**
 * Map status keys to i18n keys.
 */
const STATUS_LABEL_MAP: Record<string, I18nKey> = {
  // Recovery state
  HEALTHY: 'status.healthy',
  DEGRADED: 'status.degraded',
  CRITICAL: 'status.critical',
  // Session state
  ACTIVE: 'status.active',
  EXPIRED: 'status.expired',
  REVOKED: 'status.revoked',
  // Passkey status
};

/**
 * Get the i18n key for a status.
 * @param t - i18n translation function
 */
export function makeStatusLabel(t: (key: I18nKey) => string) {
  return (statusKey: string) => {
    const mapped = STATUS_LABEL_MAP[statusKey] ?? 'status.unknown';
    return t(mapped);
  };
}

/**
 * Get the i18n key for a status.
 * Uses the i18n context from useAppI18n.
 */
export function useStatusLabel() {
  const { t } = useAppI18n();
  return makeStatusLabel(t);
}

/**
 * Map credential device types to localized labels.
 *
 * ``backed_up`` is authoritative: a credential that reports a synced
 * backup is a synced passkey regardless of the reported device type,
 * because only a synced credential survives device loss.
 */
const PASSKEY_TYPE_MAP: Record<string, I18nKey> = {
  MULTI_DEVICE: 'sc.passkeyTypeSynced',
  SINGLE_DEVICE: 'sc.passkeyTypeDeviceBound',
  HYBRID: 'sc.passkeyTypeHybrid',
};

export function makePasskeyType(t: (key: I18nKey) => string) {
  return (deviceType?: string | null, backedUp?: boolean): string => {
    const raw = (deviceType ?? '').replace(/^CredentialDeviceType\./, '').toUpperCase();
    // A reported synced backup outranks the reported device type.
    if (backedUp) return t('sc.passkeyTypeSynced');
    return t(PASSKEY_TYPE_MAP[raw] ?? 'sc.passkeyTypeUnknown');
  };
}

export function usePasskeyType() {
  const { t } = useAppI18n();
  return makePasskeyType(t);
}

/**
 * Map relying-party profile state to a localized label.
 * ``rp_kind`` is a canonical machine value and is never mutated.
 */
const RP_KIND_MAP: Record<string, I18nKey> = {
  CURRENT: 'sc.rpCurrent',
  ACTIVE: 'sc.rpCurrent',
  PRIMARY: 'sc.rpCurrent',
  LEGACY: 'sc.rpLegacy',
  DEPRECATED: 'sc.rpLegacy',
  RETIRED: 'sc.rpLegacy',
};

export function makeRpLabel(t: (key: I18nKey) => string) {
  return (rpKind?: string | null): string => {
    const key = rpKind ? RP_KIND_MAP[rpKind.toUpperCase()] : undefined;
    return t(key ?? 'sc.rpUnknown');
  };
}

export function useRpLabel() {
  const { t } = useAppI18n();
  return makeRpLabel(t);
}

/** Map device posture trust state to a localized label. */
/**
 * Map session auth methods to localized labels.
 * The canonical machine value is never mutated.
 */
const AUTH_METHOD_MAP: Record<string, I18nKey> = {
  PASSKEY: 'sc.authMethodPasskey',
  DESKTOP_HANDOFF: 'sc.authMethodDesktopHandoff',
  PASSWORD: 'sc.authMethodPassword',
};

export function makeAuthMethodLabel(t: (key: I18nKey) => string) {
  return (value?: string | null): string => {
    const key = value ? AUTH_METHOD_MAP[value.toUpperCase()] : undefined;
    return t(key ?? 'sc.authMethodUnknown');
  };
}

export function useAuthMethodLabel() {
  const { t } = useAppI18n();
  return makeAuthMethodLabel(t);
}

/** Map session client class to a localized label. */
const CLIENT_CLASS_MAP: Record<string, I18nKey> = {
  DESKTOP: 'sc.clientClassDesktop',
  MOBILE: 'sc.clientClassMobile',
  TABLET: 'sc.clientClassTablet',
};

export function makeClientClassLabel(t: (key: I18nKey) => string) {
  return (value?: string | null): string => {
    const key = value ? CLIENT_CLASS_MAP[value.toUpperCase()] : undefined;
    return t(key ?? 'sc.clientClassUnknown');
  };
}

export function useClientClassLabel() {
  const { t } = useAppI18n();
  return makeClientClassLabel(t);
}

/**
 * Human relative time for a session timestamp.
 * Coarse on purpose; see sessionAttributionModel for the bucketing.
 */
export function makeRelativeTime(t: (key: I18nKey) => string) {
  return (minutes: number | null): string => {
    if (minutes === null) return t('sc.timeJustNow');
    if (minutes < 2) return t('sc.timeJustNow');
    if (minutes < 60) {
      return t('sc.timeMinutesAgo').replace('{{count}}', String(minutes));
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t('sc.timeHoursAgo').replace('{{count}}', String(hours));
    }
    return t('sc.timeDaysAgo').replace('{{count}}', String(Math.floor(hours / 24)));
  };
}

export function useRelativeTime() {
  const { t } = useAppI18n();
  return makeRelativeTime(t);
}

const TRUST_STATE_MAP: Record<string, I18nKey> = {
  TRUSTED: 'sc.trustTrusted',
  RESTRICTED: 'sc.trustRestricted',
  REVOKED: 'sc.trustRevoked',
  UNKNOWN: 'status.unknown',
};

export function makeTrustStateLabel(t: (key: I18nKey) => string) {
  return (state?: string | null): string => {
    const key = state ? TRUST_STATE_MAP[state.toUpperCase()] : undefined;
    return t(key ?? 'status.unknown');
  };
}

export function useTrustStateLabel() {
  const { t } = useAppI18n();
  return makeTrustStateLabel(t);
}

/** Map device posture recovery class to a localized label. */
const RECOVERY_CLASS_MAP: Record<string, I18nKey> = {
  PRIMARY: 'sc.recoveryPrimary',
  SECONDARY: 'sc.recoverySecondary',
  RECOVERY: 'sc.recoveryRecovery',
  UNKNOWN: 'status.unknown',
};

export function makeRecoveryClassLabel(t: (key: I18nKey) => string) {
  return (value?: string | null): string => {
    const key = value ? RECOVERY_CLASS_MAP[value.toUpperCase()] : undefined;
    return t(key ?? 'status.unknown');
  };
}

export function useRecoveryClassLabel() {
  const { t } = useAppI18n();
  return makeRecoveryClassLabel(t);
}

/**
 * Humanize a duration in seconds using i18n keys.
 * For common durations, use predefined keys; otherwise format numerically.
 * @param t - i18n translation function
 */
export function makeHumanDuration(t: (key: I18nKey) => string) {
  return (seconds: number) => {
    if (seconds <= 0) return t('sc.duration0');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      // Use predefined keys for common minute values
      const commonMinutes = [1, 5, 15, 30];
      const closest = commonMinutes.reduce((prev, curr) =>
        Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
      );
      if (Math.abs(closest - minutes) <= 2) {
        return t("sc.durationMinutes").replace("{{count}}", String(closest));
      }
      return t('sc.durationMinutes').replace('{{count}}', String(minutes));
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      const commonHours = [1, 2, 3, 4, 6, 8, 12];
      const closest = commonHours.reduce((prev, curr) =>
        Math.abs(curr - hours) < Math.abs(prev - hours) ? curr : prev
      );
      if (Math.abs(closest - hours) <= 1) {
        return t("sc.durationHours").replace("{{count}}", String(closest));
      }
      return t('sc.durationHours').replace('{{count}}', String(hours));
    }
    const days = Math.floor(hours / 24);
    return t('sc.durationDays').replace('{{count}}', String(days));
  };
}

/**
 * Humanize a duration in seconds using i18n keys.
 * For common durations, use predefined keys; otherwise format numerically.
 * Uses the i18n context from useAppI18n.
 */
export function useHumanDuration() {
  const { t } = useAppI18n();
  return makeHumanDuration(t);
}
